(() => {
  'use strict';

  if (window.__u20ReleaseGateRuntime) {
    window.__u20ReleaseGateRuntime.refresh(document);
    return;
  }

  const SELECTOR = '[data-u20-release-gate]';
  const RELOAD_PARAM = 'u20_release';
  const EASTERN_TIME_ZONE = 'America/New_York';
  let timer = 0;
  let reloading = false;
  let anchorEpochMs = 0;
  let anchorPerformanceMs = 0;
  let observer = null;

  const pad = value => String(Math.max(0, value)).padStart(2, '0');

  const easternDateFormatter = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: EASTERN_TIME_ZONE,
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    } catch (_) {
      return null;
    }
  })();

  const formatEasternDate = releaseEpoch => {
    if (!easternDateFormatter) return '';
    const date = new Date(releaseEpoch * 1000);
    if (Number.isNaN(date.getTime())) return '';
    const parts = easternDateFormatter.formatToParts(date);
    const readPart = type => parts.find(part => part.type === type)?.value || '';
    const label = [
      `${readPart('month')} ${readPart('day')}, ${readPart('year')}`,
      `${readPart('hour')}:${readPart('minute')} ${readPart('dayPeriod')} ${readPart('timeZoneName')}`
    ].join(' · ');
    return label.toUpperCase();
  };

  const renderReleaseDate = (node, releaseEpoch) => {
    const target = node.querySelector('[data-u20-release-date]');
    if (!target || target.dataset.u20ReleaseDateEpoch === String(releaseEpoch)) return;
    const label = formatEasternDate(releaseEpoch);
    if (!label) return;
    target.textContent = label;
    target.dataset.u20ReleaseDateEpoch = String(releaseEpoch);
  };

  const estimatedServerNow = () => {
    if (!anchorEpochMs) return 0;
    return anchorEpochMs + Math.max(0, performance.now() - anchorPerformanceMs);
  };

  const adoptServerAnchor = node => {
    const epochMs = Number(node.dataset.u20ServerNow) * 1000;
    if (!Number.isFinite(epochMs) || epochMs <= 0) return;
    const estimated = estimatedServerNow();
    if (!estimated || epochMs > estimated) {
      anchorEpochMs = epochMs;
      anchorPerformanceMs = performance.now();
    }
  };

  const reloadForRelease = releaseEpoch => {
    if (reloading) return;
    reloading = true;
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_PARAM, `${releaseEpoch}-${Math.round(performance.now())}`);
    window.location.replace(url.toString());
  };

  const renderNode = (node, nowMs) => {
    const releaseEpoch = Number(node.dataset.u20ReleaseAt);
    if (!Number.isFinite(releaseEpoch) || releaseEpoch <= 0) return Infinity;

    renderReleaseDate(node, releaseEpoch);

    const remainingSeconds = Math.max(0, Math.ceil(releaseEpoch - nowMs / 1000));
    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor((remainingSeconds % 86400) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;

    const values = [
      ['[data-u20-release-days]', days],
      ['[data-u20-release-hours]', hours],
      ['[data-u20-release-minutes]', minutes],
      ['[data-u20-release-seconds]', seconds]
    ];
    values.forEach(([selector, value]) => {
      const target = node.querySelector(selector);
      if (target) target.textContent = pad(value);
    });

    node.classList.toggle('is-expired', remainingSeconds <= 0);
    if (remainingSeconds <= 0) reloadForRelease(releaseEpoch);
    return remainingSeconds;
  };

  const tick = () => {
    window.clearTimeout(timer);
    timer = 0;
    if (document.hidden) return;
    const nodes = Array.from(document.querySelectorAll(SELECTOR));
    if (!nodes.length || reloading) return;
    nodes.forEach(adoptServerAnchor);
    const nowMs = estimatedServerNow();
    if (!nowMs) return;
    nodes.forEach(node => renderNode(node, nowMs));
    if (!reloading) {
      const delay = Math.max(120, 1000 - (nowMs % 1000) + 20);
      timer = window.setTimeout(tick, delay);
    }
  };

  const refresh = root => {
    const nodes = Array.from((root || document).querySelectorAll?.(SELECTOR) || []);
    if (!nodes.length && !document.querySelector(SELECTOR)) return;
    nodes.forEach(adoptServerAnchor);
    tick();
  };

  const destroy = () => {
    window.clearTimeout(timer);
    timer = 0;
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', onPageShow);
    observer?.disconnect();
    observer = null;
  };

  const onSectionLoad = event => refresh(event.target);
  const onVisibilityChange = () => {
    if (!document.hidden) tick();
  };
  const onPageShow = () => tick();

  window.__u20ReleaseGateRuntime = { refresh, destroy };
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', onPageShow);

  const observeInjectedGates = () => {
    if (observer || !document.body || typeof MutationObserver !== 'function') return;
    observer = new MutationObserver(records => {
      const hasNewGate = records.some(record => Array.from(record.addedNodes).some(node => (
        node.nodeType === 1
        && (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))
      )));
      if (hasNewGate) refresh(document);
    });
    observer.observe(document.body, {childList: true, subtree: true});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeInjectedGates();
      refresh(document);
    }, { once: true });
  } else {
    observeInjectedGates();
    refresh(document);
  }
})();
